import SurveyBaseController from './survey_base_controller';

export default class extends SurveyBaseController {
  static targets = ["dateFrom", "dateTo"]

  static values = {
    surveyableType: String,
    surveyableId: String,
    mode: String,
    loaded: { type: Boolean, default: false },
    reviewDecisionFilter: { type: String, default: '' }
  }

  connect() {
    super.connect();
    // Listen for the Export tab click to trigger lazy loading
    this._tabClickHandler = (event) => {
      const tab = event.target.closest('[data-tab="mission-export"]');
      if (tab) this.load();
    };
    document.addEventListener("click", this._tabClickHandler);

    // When Results dashboard period/dates change, sync to own inputs and mark for reload
    this._periodChangeHandler = () => {
      this.syncFromDashboard();
      this.loadedValue = false;
    };
    document.addEventListener("survey-dashboard:period-changed", this._periodChangeHandler);

    // Also load if the Export tab was restored from localStorage (panel is visible)
    const panel = this.element.closest("[data-persist-tabs-target='panel']");
    if (panel && !panel.classList.contains("hidden")) {
      this.load();
    }
  }

  disconnect() {
    if (this._tabClickHandler) {
      document.removeEventListener("click", this._tabClickHandler);
    }
    if (this._periodChangeHandler) {
      document.removeEventListener("survey-dashboard:period-changed", this._periodChangeHandler);
    }
    super.disconnect && super.disconnect();
  }

  reviewDecisionFilterValueChanged() {
    this._updatePillStyles();
    if (!this.loadedValue) return;
    this.loadedValue = false;
    this.load();
  }

  setDecisionFilter(event) {
    this.reviewDecisionFilterValue = event.currentTarget.dataset.decisionFilter;
  }

  _updatePillStyles() {
    const pills = this.element.querySelectorAll('[data-decision-filter]');
    pills.forEach(pill => {
      const active = pill.dataset.decisionFilter === this.reviewDecisionFilterValue;
      pill.classList.toggle('ring-2', active);
      pill.classList.toggle('ring-offset-1', active);
      pill.classList.toggle('ring-sapphire', active);
      pill.classList.toggle('font-semibold', active);
    });
  }

  // Called when the Export tab becomes visible
  async load() {
    if (this.loadedValue) return; // Already loaded
    this.loadedValue = true;

    this.survey = this.initializeSurvey(this.modeValue);

    this.vizTabPanelOptions = {
      haveCommercialLicense: true
    };

    this.surveyResults = [];
    try {
      // Show loading state
      const container = document.getElementById("survey-tabulator");
      if (container) {
        container.innerHTML = '<div class="flex items-center justify-center py-12"><div class="text-slate-400">Loading export data...</div></div>';
      }

      const surveyData = await this.loadSurveyResults();
      this.survey.fromJSON(surveyData.survey);

      // Note: ranging-status questions are NOT removed here so they appear in the Extract CSV export.
      // The dashboard_controller.js handles hiding them from Results charts separately.

      // Add export data pages - start page for primary fields, end page for additional fields
      const exportDataPageStart = this.survey.addNewPage("exportDataStart", 0);
      const exportDataPageEnd = this.survey.addNewPage("exportDataEnd", 999);

      // Primary metadata fields at the start page
      const responseIdQuestion = exportDataPageStart.addNewQuestion("text", "_response_id");
      responseIdQuestion.title = "Response ID";

      const dayOfWeekQuestion = exportDataPageStart.addNewQuestion("text", "_day_of_week");
      dayOfWeekQuestion.title = "Day of Week";

      const responseStartAtQuestion = exportDataPageStart.addNewQuestion("text", "_response_start_at");
      responseStartAtQuestion.title = "Response Start At";

      const responseEndAtQuestion = exportDataPageStart.addNewQuestion("text", "_response_end_at");
      responseEndAtQuestion.title = "Response End At";

      const missionNameQuestion = exportDataPageStart.addNewQuestion("text", "_mission_name");
      missionNameQuestion.title = "Mission Name";

      const placeNameQuestion = exportDataPageStart.addNewQuestion("text", "_place_name");
      placeNameQuestion.title = "Place Name";

      const taskerNameQuestion = exportDataPageStart.addNewQuestion("text", "_tasker_name");
      taskerNameQuestion.title = "Tasker Name";

      // Additional metadata fields at the end page
      const responseUrlQuestion = exportDataPageEnd.addNewQuestion("text", "_response_url");
      responseUrlQuestion.title = "Response URL";

      const clientIdQuestion = exportDataPageEnd.addNewQuestion("text", "_client_id");
      clientIdQuestion.title = "Client ID";

      const clientNameQuestion = exportDataPageEnd.addNewQuestion("text", "_client_name");
      clientNameQuestion.title = "Client Name";

      const campaignIdQuestion = exportDataPageEnd.addNewQuestion("text", "_campaign_id");
      campaignIdQuestion.title = "Campaign ID";

      const campaignNameQuestion = exportDataPageEnd.addNewQuestion("text", "_campaign_name");
      campaignNameQuestion.title = "Campaign Name";

      const missionIdQuestion = exportDataPageEnd.addNewQuestion("text", "_mission_id");
      missionIdQuestion.title = "Mission ID";

      const taskerIdQuestion = exportDataPageEnd.addNewQuestion("text", "_tasker_id");
      taskerIdQuestion.title = "Tasker ID";

      const placeIdQuestion = exportDataPageEnd.addNewQuestion("text", "_place_id");
      placeIdQuestion.title = "Place ID";

      const placeAddressQuestion = exportDataPageEnd.addNewQuestion("text", "_place_address");
      placeAddressQuestion.title = "Place Address";

      const placeStateQuestion = exportDataPageEnd.addNewQuestion("text", "_place_state");
      placeStateQuestion.title = "Place State";

      const placeBannerIdQuestion = exportDataPageEnd.addNewQuestion("text", "_banner_id");
      placeBannerIdQuestion.title = "Banner ID";

      const placeBannerNameQuestion = exportDataPageEnd.addNewQuestion("text", "_banner_name");
      placeBannerNameQuestion.title = "Banner Name";

      const placeOwnerIdQuestion = exportDataPageEnd.addNewQuestion("text", "_place_owner_id");
      placeOwnerIdQuestion.title = "Place Owner ID";

      const placeOwnerNameQuestion = exportDataPageEnd.addNewQuestion("text", "_place_owner_name");
      placeOwnerNameQuestion.title = "Place Owner Name";

      const placeGscIdQuestion = exportDataPageEnd.addNewQuestion("text", "_place_gsc_id");
      placeGscIdQuestion.title = "Place GSC ID";

      const responseStartDateQuestion = exportDataPageEnd.addNewQuestion("text", "_response_start_date");
      responseStartDateQuestion.title = "Response Start Date";

      const responseStartTimeQuestion = exportDataPageEnd.addNewQuestion("text", "_response_start_time");
      responseStartTimeQuestion.title = "Response Start Time";

      const responseEndDateQuestion = exportDataPageEnd.addNewQuestion("text", "_response_end_date");
      responseEndDateQuestion.title = "Response End Date";

      const responseEndTimeQuestion = exportDataPageEnd.addNewQuestion("text", "_response_end_time");
      responseEndTimeQuestion.title = "Response End Time";

      const shiftIdQuestion = exportDataPageEnd.addNewQuestion("text", "_shift_id");
      shiftIdQuestion.title = "Shift ID";

      const reviewStatusQuestion = exportDataPageEnd.addNewQuestion("text", "_review_status");
      reviewStatusQuestion.title = "Review Status";

      const notesQuestion = exportDataPageEnd.addNewQuestion("text", "_notes");
      notesQuestion.title = "Notes";

      this.surveyResults = surveyData.results;
      this.renderVizTabPanel();
    } catch (error) {
      console.error("Failed to load survey results:", error);
      const container = document.getElementById("survey-tabulator");
      if (container) {
        container.innerHTML = '<div class="flex items-center justify-center py-12"><div class="text-red-500">Failed to load export data. Please try again.</div></div>';
      }
      this.loadedValue = false; // Allow retry
    }
  }

  // Sync Export panel's own date inputs from the Results dashboard controls.
  // Called on period-change events so Export always reflects what Results shows.
  syncFromDashboard() {
    if (!this.hasDateFromTarget || !this.hasDateToTarget) return;
    const dashFrom = document.querySelector('[data-survey-dashboard-target="dateFrom"]');
    const dashTo = document.querySelector('[data-survey-dashboard-target="dateTo"]');
    if (dashFrom) this.dateFromTarget.value = dashFrom.value;
    if (dashTo) this.dateToTarget.value = dashTo.value;
  }

  // Called when the user changes date inputs on the Export panel directly.
  async dateChanged() {
    if (!this.loadedValue) return;
    this.loadedValue = false;
    await this.load();
  }

  get currentPeriod() {
    if (this.currentDateFrom || this.currentDateTo) return "custom";
    const dashboardSelect = document.querySelector('[data-survey-dashboard-target="periodSelect"]');
    return dashboardSelect ? dashboardSelect.value : "today";
  }

  get currentDateFrom() {
    if (this.hasDateFromTarget) return this.dateFromTarget.value;
    const input = document.querySelector('[data-survey-dashboard-target="dateFrom"]');
    return input ? input.value : "";
  }

  get currentDateTo() {
    if (this.hasDateToTarget) return this.dateToTarget.value;
    const input = document.querySelector('[data-survey-dashboard-target="dateTo"]');
    return input ? input.value : "";
  }

  async loadSurveyResults() {
    try {
      let url = `/api/v1/surveys/results?surveyable_id=${this.surveyableIdValue}&surveyable_type=${this.surveyableTypeValue}&period=${this.currentPeriod}`;
      if (this.currentDateFrom) url += `&date_from=${this.currentDateFrom}`;
      if (this.currentDateTo) url += `&date_to=${this.currentDateTo}`;
      if (this.reviewDecisionFilterValue) url += `&review_decision=${this.reviewDecisionFilterValue}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8'
        }
      });

      if (!response.ok) throw new Error("Failed to fetch survey results");

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error loading survey:", error);
      throw error;
    }
  }

  renderVizTabPanel() {
    this.vizTabPanel = new SurveyAnalyticsTabulator.Tabulator(
      this.survey,
      this.surveyResults,
      this.vizTabPanelOptions
    );

    const tabPanelContainer = document.getElementById("survey-tabulator");
    if (tabPanelContainer) {
      this.vizTabPanel.render(tabPanelContainer);
    }
  }
}
